import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { ORDER_STATUS_DEFINITIONS, type OrderStatus } from "@laundrypro/shared";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorText,
  Input,
  Modal,
  PageHeader,
  Skeleton,
  Table,
  Td,
  Th,
  THead,
  Tr,
  useToast,
} from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { ApiError, apiFetch } from "../lib/api";
import { currencyLabel, dateTime, money } from "../lib/format";

interface OrderItem {
  id: string;
  itemTypeNameAr: string;
  itemTypeNameEn: string;
  serviceNameAr: string;
  serviceNameEn: string;
  quantity: number;
  unitPriceMinor: number;
  discountMinor: number;
  taxMinor: number;
  lineTotalMinor: number;
  notes: string | null;
}

interface StatusEntry {
  id: string;
  fromStatus: OrderStatus | null;
  toStatus: OrderStatus;
  reason: string | null;
  actorName: string | null;
  createdAt: string;
}

interface OrderDetail {
  id: string;
  orderRef: string;
  status: OrderStatus;
  allowedTransitions: OrderStatus[];
  customer: { id: string; name: string; mobile: string | null } | null;
  receivedAt: string;
  expectedReadyAt: string | null;
  completedAt: string | null;
  deliveredAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  notes: string | null;
  subtotalMinor: number;
  taxMinor: number;
  totalMinor: number;
  amountPaidMinor: number;
  balanceDueMinor: number;
  items: OrderItem[];
  statusHistory: StatusEntry[];
}

/**
 * Order detail.
 *
 * The status controls render ONLY the transitions the server said are allowed
 * (`allowedTransitions` in the payload). The interface never invents a move,
 * and when the server refuses one anyway — someone else already changed the
 * order — the error is shown and the order reloaded, never optimistically
 * pretended.
 */
export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const { language } = useBranding();
  const { showToast } = useToast();
  const ar = language === "ar";
  const currency = currencyLabel(language);

  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState<OrderStatus | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoadError(null);
    try {
      setOrder(await apiFetch<OrderDetail>(`/orders/${id}`));
    } catch (err) {
      setLoadError(
        err instanceof ApiError && err.status === 404
          ? ar
            ? "الطلب غير موجود."
            : "This order does not exist."
          : err instanceof ApiError
            ? err.message
            : ar
              ? "تعذّر تحميل الطلب."
              : "Couldn't load this order.",
      );
    }
  }, [id, ar]);

  useEffect(() => {
    void load();
  }, [load]);

  const changeStatus = async (to: OrderStatus, reason?: string) => {
    setTransitionError(null);
    setTransitioning(to);
    try {
      await apiFetch(`/orders/${id}/status`, { method: "POST", body: { status: to, reason: reason ?? null } });
      const def = ORDER_STATUS_DEFINITIONS[to];
      showToast("success", ar ? `أصبح الطلب «${def.labelAr}».` : `Order is now ${def.labelEn.toLowerCase()}.`);
      setCancelOpen(false);
      setCancelReason("");
      await load();
    } catch (err) {
      // The server refused. Show why, and RELOAD — the refusal usually means
      // the order changed under us, and the stale screen is the real problem.
      setTransitionError(
        err instanceof ApiError ? err.message : ar ? "تعذّر تغيير الحالة." : "Couldn't change the status.",
      );
      await load();
    } finally {
      setTransitioning(null);
    }
  };

  if (loadError) {
    return (
      <div className="space-y-4">
        <PageHeader title={ar ? "الطلبات" : "Orders"} />
        <Card>
          <EmptyState
            icon={<AlertTriangle className="size-6" />}
            title={ar ? "تعذّر تحميل الطلب" : "Couldn't load this order"}
            description={loadError}
            action={
              <Button size="sm" onClick={load}>
                {ar ? "إعادة المحاولة" : "Retry"}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-7 w-40" />
        <Card>
          <Skeleton className="h-32 w-full" />
        </Card>
        <Card>
          <Skeleton className="h-48 w-full" />
        </Card>
      </div>
    );
  }

  const def = ORDER_STATUS_DEFINITIONS[order.status];
  const forwardTransitions = order.allowedTransitions.filter((t) => t !== "CANCELLED");
  const canCancel = order.allowedTransitions.includes("CANCELLED") && hasPermission("orders.cancel");
  const canUpdate = hasPermission("orders.update");

  return (
    <div className="space-y-4">
      <PageHeader
        title={order.orderRef}
        subtitle={order.customer ? order.customer.name : undefined}
        trailing={<Badge tone={def.tone}>{ar ? def.labelAr : def.labelEn}</Badge>}
      />

      {/* Status actions — driven entirely by allowedTransitions. */}
      {(forwardTransitions.length > 0 || canCancel) && (
        <Card>
          <CardHeader
            title={ar ? "تغيير الحالة" : "Move the order forward"}
            description={ar ? def.descriptionAr : def.descriptionEn}
          />
          <div className="flex flex-wrap items-center gap-2">
            {canUpdate &&
              forwardTransitions.map((to) => {
                const target = ORDER_STATUS_DEFINITIONS[to];
                return (
                  <Button
                    key={to}
                    type="button"
                    loading={transitioning === to}
                    onClick={() => changeStatus(to)}
                  >
                    {ar ? target.labelAr : target.labelEn}
                  </Button>
                );
              })}
            {canCancel && (
              <Button type="button" variant="danger" onClick={() => setCancelOpen(true)}>
                {ar ? "إلغاء الطلب" : "Cancel order"}
              </Button>
            )}
          </div>
          {transitionError && (
            <div className="mt-3">
              <ErrorText>{transitionError}</ErrorText>
            </div>
          )}
        </Card>
      )}

      <Card padding="none">
        <div className="p-5 pb-0 sm:p-6 sm:pb-0">
          <CardHeader title={ar ? "الأصناف" : "Items"} />
        </div>
        <div className="px-5 pb-5 sm:px-6 sm:pb-6">
          <Table>
            <THead>
              <tr>
                <Th>{ar ? "الصنف" : "Item"}</Th>
                <Th>{ar ? "الخدمة" : "Service"}</Th>
                <Th>{ar ? "الكمية" : "Qty"}</Th>
                <Th>{ar ? "سعر الوحدة" : "Unit price"}</Th>
                <Th>{ar ? "الإجمالي" : "Total"}</Th>
              </tr>
            </THead>
            <tbody>
              {order.items.map((item) => (
                <Tr key={item.id}>
                  <Td className="font-medium text-ink-900">
                    {ar ? item.itemTypeNameAr : item.itemTypeNameEn}
                    {item.notes && <span className="block text-xs text-muted-foreground">{item.notes}</span>}
                  </Td>
                  <Td>{ar ? item.serviceNameAr : item.serviceNameEn}</Td>
                  <Td className="tabular-nums">{item.quantity}</Td>
                  <Td className="tabular-nums">{money(item.unitPriceMinor)}</Td>
                  <Td className="tabular-nums font-medium">{money(item.lineTotalMinor)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title={ar ? "المبالغ" : "Amounts"} />
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between gap-3 py-1">
              <span className="text-muted-foreground">{ar ? "قبل الضريبة" : "Subtotal (excl. VAT)"}</span>
              <span className="tabular-nums">{money(order.subtotalMinor)}</span>
            </div>
            <div className="flex justify-between gap-3 py-1">
              <span className="text-muted-foreground">{ar ? "الضريبة" : "VAT"}</span>
              <span className="tabular-nums">{money(order.taxMinor)}</span>
            </div>
            <div className="flex justify-between gap-3 border-t-2 border-ink-900 py-2 text-base font-bold">
              <span>{ar ? "الإجمالي" : "Total"}</span>
              <span className="tabular-nums">
                {money(order.totalMinor)} {currency}
              </span>
            </div>
            <div className="flex justify-between gap-3 py-1">
              <span className="text-muted-foreground">{ar ? "المدفوع" : "Paid"}</span>
              <span className="tabular-nums">{money(order.amountPaidMinor)}</span>
            </div>
            <div className="flex justify-between gap-3 py-1">
              <span className="text-muted-foreground">{ar ? "المستحق" : "Balance due"}</span>
              <span
                className={`tabular-nums font-medium ${order.balanceDueMinor > 0 ? "text-warning" : "text-ink-900"}`}
              >
                {money(order.balanceDueMinor)} {currency}
              </span>
            </div>
          </div>
        </Card>

        <Card>
          <CardHeader title={ar ? "سجل الحالة" : "Status history"} />
          <ul className="space-y-2 text-sm">
            {order.statusHistory.map((entry) => {
              const target = ORDER_STATUS_DEFINITIONS[entry.toStatus];
              return (
                <li key={entry.id} className="flex items-start justify-between gap-3 border-b border-line-hairline pb-2 last:border-0">
                  <div className="min-w-0">
                    <Badge tone={target.tone}>{ar ? target.labelAr : target.labelEn}</Badge>
                    {entry.reason && (
                      <span className="mt-1 block text-xs text-muted-foreground">{entry.reason}</span>
                    )}
                  </div>
                  <div className="shrink-0 text-end text-xs text-muted-subtle">
                    <span className="tabular-nums block">{dateTime(entry.createdAt, language)}</span>
                    {entry.actorName && <span className="block">{entry.actorName}</span>}
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>

      {order.customer && (
        <p className="text-xs text-muted-foreground">
          {ar ? "العميل: " : "Customer: "}
          <Link to={`/customers/${order.customer.id}`} className="text-accent-600 hover:underline">
            {order.customer.name}
          </Link>
          {order.customer.mobile && <span className="tabular-nums"> · {order.customer.mobile}</span>}
        </p>
      )}

      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title={ar ? "إلغاء الطلب" : "Cancel this order"}
        closeLabel={ar ? "إغلاق" : "Close"}
      >
        <p className="text-sm text-ink-700">
          {ar
            ? "سيُستبعد الطلب من المستحقات ولا يمكن التراجع عن الإلغاء."
            : "The order will be excluded from balances. Cancellation cannot be undone."}
        </p>
        <div className="mt-3">
          <Input
            label={ar ? "سبب الإلغاء" : "Reason"}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            required
          />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => setCancelOpen(false)}>
            {ar ? "تراجع" : "Keep the order"}
          </Button>
          <Button
            type="button"
            variant="danger"
            loading={transitioning === "CANCELLED"}
            disabled={cancelReason.trim().length < 3}
            onClick={() => changeStatus("CANCELLED", cancelReason.trim())}
          >
            {ar ? "تأكيد الإلغاء" : "Cancel order"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
